'use client';
import styles from './gameCard.module.scss';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { Text } from '@/ui-components/atoms/Text/Text';
import cn from 'classnames';
import { Link } from '@/ui-components/atoms/Link/Link';
import { colorBrightness } from '@/packages/color/colorBrightness';
import dayjs from 'dayjs';
import { useState, useRef, useEffect } from 'react';
import { IconDropDown } from '@/assets/icons/IconDropdown';
import { DynamicTableRow } from '../DynamicTable/DynamicTable';
import { DeviceCar } from '@/assets/icons/DeviceCar';
import { DeviceMultiple } from '@/assets/icons/DeviceMultiple';
import useClickOutside from '@/hooks/useClickOutside';
import { responsiveBreakpointsPx } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { Button } from '@/ui-components/atoms/Button/Button';
import { MoreWaysToListen } from '../MoreWaysToListen/MoreWaysToListen';

const HALF_LUMINANCE = 255 / 2;

export interface Team {
    image: React.ComponentProps<typeof ImageWrapper>;
    title: string;
    subtitle?: string;
    backgroundColor: string;
    channel?: string;
    isOnline?: boolean;
    link: React.ComponentProps<typeof Link>;
    nationalChannel?: string;
    spanishChannel?: string;
    channelStream?: string;
    nationalStream?: string;
    spanishStream?: string;
    channelStreamLink?: string;
    nationalStreamLink?: string;
    spanishStreamLink?: string;
}

export type GameCardTeamTupleType = [Team, Team?];

export interface GameCardProps {
    teams: GameCardTeamTupleType;
    dateTime: Date;
    link?: React.ComponentProps<typeof Link>;
    linkText?: string;
    firstCopyright?: string;
    secondCopyright?: string;
    useContrastingImageColors?: boolean;
}

const getDateTime = (date: Date) => {
    const dt = dayjs(date);
    const matchupDay = dt.format('ddd, MMM D');
    const matchupTime = dt.format('h:mm A');
    const timezone = new Date(date).toLocaleTimeString('en-us', { timeZoneName: 'short' }).split(' ')[2];
    return `${matchupDay.toUpperCase()} · ${matchupTime.toUpperCase()} ${timezone}`;
};

export const GameCard = ({
    teams,
    dateTime,
    link,
    linkText,
    firstCopyright,
    secondCopyright,
    useContrastingImageColors,
}: GameCardProps) => {
    const [calculatedDateTime, setCalculatedDateTime] = useState('');
    const style: React.CSSProperties = {
        ...({ '--background1': teams[0].backgroundColor } as React.CSSProperties),
        ...({ '--background2': teams[1]?.backgroundColor } as React.CSSProperties),
    };

    const [iconColor1, iconColor2] = teams.map(
        (team, index) =>
            (team &&
                styles[
                    `team${index + 1}Icon${colorBrightness(team.backgroundColor) > HALF_LUMINANCE ? 'Black' : 'White'}`
                ]) ??
            '',
    );
    const imageClasses = cn(styles.imageRow, {
        [styles.dual]: teams.length === 2,
        [styles.single]: teams.length === 1,
        [iconColor1 ?? '']: useContrastingImageColors,
        [iconColor2 ?? '']: useContrastingImageColors,
    });

    const infoClasses = cn({
        [styles.dualInfo]: teams.length === 2,
        [styles.singleInfo]: teams.length === 1,
    });

    const [isLeftModalOpen, setLeftModalOpen] = useState(false);
    const [isRightModalOpen, setRightModalOpen] = useState(false);
    const viewportIsDesktopSized = useMediaQuery(`(min-width: ${responsiveBreakpointsPx.md}px)`);

    function handleToggleLeft() {
        setRightModalOpen(false);
        setLeftModalOpen(!isLeftModalOpen);
    }

    function handleToggleRight() {
        setLeftModalOpen(false);
        setRightModalOpen(!isRightModalOpen);
    }

    const moreWaysLeft: DynamicTableRow[] = [];
    const moreWaysRight: DynamicTableRow[] = [];

    function renderChannelLink(
        deepLink: string | undefined,
        channelNumber: string | number | undefined,
        ariaLabel: string,
    ) {
        if (deepLink) {
            return (
                <Button
                    type="button"
                    variant="secondary"
                    fitting="hug-contents"
                    href={deepLink}
                    size="x-small"
                    ariaLabel={ariaLabel}
                >
                    CH {channelNumber}
                </Button>
            );
        }
        return channelNumber ? `CH ${channelNumber}` : '--';
    }

    if ((teams[0].channel || teams[0].channelStream) && teams[1]) {
        const ariaLabel = `Listen to ${teams[0].title} on channel ${teams[0].channelStream}`;
        moreWaysLeft.push({
            rowLabel: teams[0].title,
            items: [
                teams[0].channel ? `CH ${teams[0].channel}` : '--',
                renderChannelLink(teams[0].channelStreamLink, teams[0].channelStream, ariaLabel),
            ],
        });
    }

    if (teams[0].nationalChannel || teams[0].nationalStream) {
        const ariaLabel = `Listen to ${teams[0].title} on channel ${teams[0].nationalStream}`;
        moreWaysLeft.push({
            rowLabel: 'National',
            items: [
                teams[0].nationalChannel ? `CH ${teams[0].nationalChannel}` : '--',
                renderChannelLink(teams[0].nationalStreamLink, teams[0].nationalStream, ariaLabel),
            ],
        });
    }

    if (teams[0].spanishChannel || teams[0].spanishStream) {
        const ariaLabel = `Listen to ${teams[0].title} on channel ${teams[0].spanishStream}`;
        moreWaysLeft.push({
            rowLabel: 'Spanish',
            items: [
                teams[0].spanishChannel ? `CH ${teams[0].spanishChannel}` : '--',
                renderChannelLink(teams[0].spanishStreamLink, teams[0].spanishStream, ariaLabel),
            ],
        });
    }

    if (teams[1] && (teams[1].channel || teams[1].channelStream)) {
        const ariaLabel = `Listen to ${teams[1].title} on channel ${teams[1].channelStream}`;
        moreWaysRight.push({
            rowLabel: teams[1].title,
            items: [
                teams[1].channel ? `CH ${teams[1].channel}` : '--',
                renderChannelLink(teams[1].channelStreamLink, teams[1].channelStream, ariaLabel),
            ],
        });
    }

    if (teams[1] && (teams[1].nationalChannel || teams[1].nationalStream)) {
        const ariaLabel = `Listen to ${teams[1].title} on channel ${teams[1].nationalStream}`;
        moreWaysRight.push({
            rowLabel: 'National',
            items: [
                teams[1].nationalChannel ? `CH ${teams[1].nationalChannel}` : '--',
                renderChannelLink(teams[1].nationalStreamLink, teams[1].nationalStream, ariaLabel),
            ],
        });
    }

    if (teams[1] && (teams[1].spanishChannel || teams[1].spanishStream)) {
        const ariaLabel = `Listen to ${teams[1].title} on channel ${teams[1].spanishStream}`;
        moreWaysRight.push({
            rowLabel: 'Spanish',
            items: [
                teams[1].spanishChannel ? `CH ${teams[1].spanishChannel}` : '--',
                renderChannelLink(teams[1].spanishStreamLink, teams[1].spanishStream, ariaLabel),
            ],
        });
    }

    const showButtonCheck = (team: Team | undefined) =>
        !!(
            team?.channel ||
            team?.channelStream ||
            team?.nationalChannel ||
            team?.nationalStream ||
            team?.spanishChannel ||
            team?.spanishStream
        );

    const headlineChannel = (team: Team | undefined) =>
        team?.isOnline ? team.channelStream || team.nationalStream || team.spanishStream : team?.channel;

    const firstHeadlineChannel = headlineChannel(teams[0]);
    const secondHeadlineChannel = headlineChannel(teams[1]);

    const leftRef = useRef<HTMLDivElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);
    const leftButtonRef = useRef<HTMLButtonElement>(null);
    const rightButtonRef = useRef<HTMLButtonElement>(null);

    useClickOutside(
        leftRef,
        {},
        () => {
            setLeftModalOpen(false);
        },
        leftButtonRef,
    );

    useClickOutside(
        rightRef,
        {},
        () => {
            setRightModalOpen(false);
        },
        rightButtonRef,
    );

    useBodyScrollLock((isLeftModalOpen || isRightModalOpen) && !viewportIsDesktopSized);

    useEffect(() => {
        setCalculatedDateTime(getDateTime(dateTime));
    }, [dateTime]);

    return (
        <div className={styles.wrapper} data-cy="game-card">
            <div className={styles.container} style={style}>
                {teams.length === 2 ? (
                    <>
                        {teams[0]?.link && <Link {...teams[0].link} className={styles.hoverDetectorLeft} />}
                        {teams[1]?.link && <Link {...teams[1].link} className={styles.hoverDetectorRight} />}
                    </>
                ) : (
                    <>{teams[0]?.link && <Link {...teams[0].link} className={styles.singleLink} />}</>
                )}
                <div className={imageClasses}>
                    {teams.map((t, i) => (
                        <div key={i}>
                            <ImageWrapper
                                src={t?.image.src || ''}
                                alt={t?.image.alt || ''}
                                ariaLabel={t?.image.ariaLabel}
                                ariaHidden={t?.image.ariaHidden}
                                className={teams.length === 2 ? (i === 0 ? styles.leftImage : styles.rightImage) : ''}
                            />
                        </div>
                    ))}
                </div>
                {(firstCopyright || secondCopyright) && (
                    <div className={styles.copyright}>
                        {firstCopyright}
                        {firstCopyright && secondCopyright && secondCopyright.trim() !== '' && '; '}
                        {secondCopyright}
                    </div>
                )}
                <div
                    className={cn(styles.infoCol, {
                        [styles.teamsInfoCol]: teams.length === 2,
                    })}
                >
                    <div className={styles.date}>{calculatedDateTime}</div>
                    <div
                        className={cn(styles.infoRow, {
                            [styles.teamsInfoRow]: teams.length === 2,
                        })}
                    >
                        {teams.map((t, i) => (
                            <div className={infoClasses} key={i}>
                                <div>
                                    <Text
                                        tag="h6"
                                        className={
                                            teams.length === 2 ? (i === 0 ? styles.leftTitle : styles.rightTitle) : ''
                                        }
                                    >
                                        {t?.title}
                                    </Text>
                                    {t?.subtitle && !/^(home|away)$/i.test(t.subtitle) && (
                                        <Text tag="p">{t.subtitle}</Text>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.cardChannel}>
                        {teams.length === 2 ? (
                            <>
                                {showButtonCheck(teams[0]) ? (
                                    <button
                                        ref={leftButtonRef}
                                        className={styles.cardButton}
                                        onClick={() => handleToggleLeft()}
                                        type="button"
                                        data-cy="game-card-channel-left"
                                    >
                                        <div className={styles.cardIcon}>
                                            {teams[0]?.isOnline ? (
                                                <DeviceMultiple className={styles.rowIcon} />
                                            ) : (
                                                <DeviceCar className={styles.rowIcon} />
                                            )}
                                        </div>
                                        <div className={styles.cardChannelNum}>
                                            {firstHeadlineChannel ? `CH ${firstHeadlineChannel}` : '--'}
                                        </div>
                                        <div className={styles.cardChannelNumTablet}>
                                            {firstHeadlineChannel || '--'}
                                        </div>
                                        <div
                                            className={`${styles.cardDropdownLeft} ${isLeftModalOpen ? styles.active : ''}`}
                                        >
                                            <IconDropDown />
                                        </div>
                                    </button>
                                ) : (
                                    <span className={styles.emptyChannel}>--</span>
                                )}
                                {showButtonCheck(teams[1]) ? (
                                    <button
                                        ref={rightButtonRef}
                                        className={styles.cardButton}
                                        onClick={() => handleToggleRight()}
                                        type="button"
                                    >
                                        <div className={styles.cardIcon}>
                                            {teams[1]?.isOnline ? (
                                                <DeviceMultiple className={styles.rowIcon} />
                                            ) : (
                                                <DeviceCar className={styles.rowIcon} />
                                            )}
                                        </div>
                                        <div className={styles.cardChannelNum}>
                                            {secondHeadlineChannel ? `CH ${secondHeadlineChannel}` : '--'}
                                        </div>
                                        <div className={styles.cardChannelNumTablet}>
                                            {secondHeadlineChannel || '--'}
                                        </div>
                                        <div
                                            className={`${styles.cardDropdownRight} ${isRightModalOpen ? styles.active : ''}`}
                                        >
                                            <IconDropDown />
                                        </div>
                                    </button>
                                ) : (
                                    <span className={styles.emptyChannel}>--</span>
                                )}
                            </>
                        ) : showButtonCheck(teams[0]) ? (
                            <button
                                ref={leftButtonRef}
                                className={`${styles.cardButton} ${styles.single}`}
                                onClick={() => handleToggleLeft()}
                                type="button"
                                data-cy="game-card-channel-left"
                            >
                                <div className={styles.cardIcon}>
                                    {teams[0]?.isOnline ? (
                                        <DeviceMultiple className={styles.rowIcon} />
                                    ) : (
                                        <DeviceCar className={styles.rowIcon} />
                                    )}
                                </div>
                                <div className={styles.cardChannelNum}>
                                    {firstHeadlineChannel ? `CH ${firstHeadlineChannel}` : '--'}
                                </div>
                                <div className={styles.cardChannelNumTablet}>{firstHeadlineChannel || '--'}</div>
                                <div className={`${styles.cardDropdownLeft} ${isLeftModalOpen ? styles.active : ''}`}>
                                    <IconDropDown />
                                </div>
                            </button>
                        ) : (
                            <span className={styles.emptyChannel}>--</span>
                        )}
                    </div>
                </div>
            </div>
            {isLeftModalOpen && (
                <MoreWaysToListen
                    variant="game-card"
                    renderAs={viewportIsDesktopSized ? 'desktop' : 'mobile'}
                    rows={moreWaysLeft}
                    desktopRef={leftRef}
                    desktopClassName={styles.modalDesktop}
                    mobileTitle="Ways to Listen"
                    onMobileClose={() => setLeftModalOpen(false)}
                />
            )}
            {isRightModalOpen && (
                <MoreWaysToListen
                    variant="game-card"
                    renderAs={viewportIsDesktopSized ? 'desktop' : 'mobile'}
                    rows={moreWaysRight}
                    desktopRef={rightRef}
                    desktopClassName={styles.modalDesktop}
                    mobileTitle="Ways to Listen"
                    onMobileClose={() => setRightModalOpen(false)}
                />
            )}
            {link && (
                <div className={styles.bottomLink}>
                    <Link {...link}>{linkText}</Link>
                </div>
            )}
        </div>
    );
};
